import classNames from "classnames";
import { BlockedButton } from "components/BlockedButton/BlockedButton";
import { EditBankAccount } from "components/Connections/EditBankAccount";
import { ReConnect } from "components/Connections/ReConnect";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ToolTip from "components/design/toolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Info } from "components/icons/Info";
import { ActionRequiredPopOver } from "components/ActionRequiredPopover/ActionRequiredPopover";
import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import { DashboardLayout } from "components/DashboardLayout";
import { Header } from "components/DesignSystem/Header/Header";
import Tab from "components/DesignSystem/Tab/Tab";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import { MultiValue, SingleValue } from "react-select";
import { Diamond } from "components/icons/Diamond";
import { LockedUi } from "components/LockedUi/LockedUi";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import {
  BANK_CONNECTED_BROADCAST_MESSAGE,
  BANK_CONNECTION_MODAL,
  BANK_CONNECTION_SUCCESS_BROADCAST_CHANNEL,
} from "constants/connections";
import {
  ACCOUNT_CONNECTION_TYPE,
  AccountConnectionType,
  BANK_ACCOUNT_STATUS,
  GUSTO,
} from "constants/dataSource";
import * as RECONCILLATION_STATUS from "constants/reconcilliationStatus";
import { CONNECT_BANK_ACCOUNT } from "constants/subscriptionPermissionFeatures";
import dayjs from "dayjs";
import RelativeTime from "dayjs/plugin/relativeTime";
import { motion } from "framer-motion";
import { useActionItems } from "hooks/useActionItems";
import { useBankConnect } from "hooks/useBankConnect";
import { useBroadcastChannel } from "hooks/useBroadcastChannel";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDataSourceSegregate } from "hooks/useDataSourceSegregate";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { ReactNode } from "react";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import BankCircular from "static/images/BankCircular.svg";
import EmptyDataSource from "static/images/EmptyDataSource.svg";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { Connection } from "store/apis/booksConnections";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { BankConnection } from "types/Models/banks";
import { pluralize } from "utils/pluralize";
import { EmptyScreen } from "../EmptyScreen";
import { ModalDescriptionWrapper } from "../RevenueMetrics/RevenueMetricsLocked";
import { AllAccount } from "./AllAccount/AllAccount";
import { BankAndCreditCard } from "./BankAndCreditCard/BankAndCreditCard";
import ConnectDataSourcesRoot from "./ConnectDataSrourcesRoot";
import { DataSourceView } from "./DataSourceView/DataSourceView";
import GustoSlider from "./Gusto/GustoSlider";
import { Payroll } from "./Payroll/Payroll";
import { Revenue } from "./Revenue/Revenue";
import { APandAR } from "./APandAR/APandAR";
import { BillComSlider } from "./APandAR/BillComSlider";
import { DISPLAY_DATE } from "constants/date";
import { getDisconnectedMessage } from "utils/bankNameAcc";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

dayjs.extend(RelativeTime);

export const AccountCardsSection = ({ children }: { children: ReactNode }) => {
  return (
    <motion.div
      layout
      className="t-grid t-grid-cols-[repeat(auto-fill,minmax(270px,1fr))] t-gap-x-6 t-gap-y-14"
    >
      {children}
    </motion.div>
  );
};

export const RevenueCard = ({
  stripeConnectedBank,
  stripeConnection,
}: {
  stripeConnection: Connection;
  stripeConnectedBank?: BankConnection;
}) => {
  const { url } = useRouteMatch();
  const { search } = useLocation();
  const history = useHistory();

  const {
    onConnect,
    isLoading: isConnectBankLoading,
    originalArgs,
  } = useBankConnect();

  if (!stripeConnectedBank) {
    return null;
  }

  const {
    disconnectedStatus,
    hasLongerName,
    isReconciliationRequired,
    reconcilliationRequiredStatus,
    updatedAt,
    isAllAccountsClosed,
  } = getConnectionInfoItem({ connection: stripeConnectedBank });

  const disconnectedMessage = getDisconnectedMessage(stripeConnectedBank);

  const description = isAllAccountsClosed ? (
    <span className="t-text-red-70">No active accounts</span>
  ) : (
    <span
      className={classNames("", {
        "t-text-green-80": stripeConnectedBank.is_connection_available,
        "t-text-red-70": !stripeConnectedBank.is_connection_available,
      })}
    >
      {stripeConnectedBank.is_connection_available
        ? `${pluralize(
            stripeConnectedBank.bank_accounts.filter(
              ({ status }) => status === BANK_ACCOUNT_STATUS.ACTIVE
            ).length,
            "account",
            "accounts"
          )} connected`
        : disconnectedMessage}
    </span>
  );

  return (
    <button
      key={stripeConnectedBank.uuid}
      className="all:unset"
      onClick={() =>
        history.push(`${url}/${stripeConnectedBank.uuid}${search}`)
      }
    >
      <ConnectionCard
        status={reconcilliationRequiredStatus || disconnectedStatus}
        logoUrl={stripeConnectedBank.logo_url}
        disconnected={
          !stripeConnectedBank.is_connection_available ||
          isReconciliationRequired
        }
        name={
          <div
            className={classNames(
              "t-flex t-gap-1 t-items-center t-text-center",
              {
                "t-flex-col": hasLongerName,
              }
            )}
          >
            <span
              className={classNames({
                "t-order-2": hasLongerName,
                "t-order-1": !hasLongerName,
              })}
            >
              {stripeConnectedBank.name}
            </span>
          </div>
        }
        tag={
          <div>
            <span
              className={classNames({
                "t-order-1": hasLongerName,
                "t-order-2": !hasLongerName,
                "t-visible":
                  stripeConnectedBank.connection_type === "MANUAL" ||
                  stripeConnectedBank.connection_type === "PLAID",
                "t-invisible":
                  stripeConnectedBank.connection_type !== "MANUAL" &&
                  stripeConnectedBank.connection_type !== "PLAID",
              })}
            >
              <Tag size="small" rounded icon={false} tagType="light_grey">
                {stripeConnectedBank.connection_type === "MANUAL"
                  ? "Manually added"
                  : stripeConnectedBank.connection_type === "PLAID"
                  ? "Via plaid"
                  : "direct"}
              </Tag>
            </span>
          </div>
        }
        description={description}
        updatedAt={isAllAccountsClosed ? undefined : updatedAt}
        total={
          isAllAccountsClosed
            ? undefined
            : stripeConnectedBank.bank_accounts?.reduce((acc, account) => {
                return account.status === BANK_ACCOUNT_STATUS.CLOSED
                  ? acc
                  : acc + account.available_balance;
              }, 0)
        }
        CTA={
          isAllAccountsClosed
            ? undefined
            : !stripeConnectedBank.is_connection_available && (
                <ReConnect
                  isLoading={
                    isConnectBankLoading &&
                    originalArgs?.connectionId ===
                      stripeConnectedBank.direct_base_connection_id
                  }
                  onConnect={onConnect}
                  connection={stripeConnectedBank}
                />
              )
        }
      />
    </button>
  );
};

export const AccountCard = ({
  logo,
  description,
  title,
  CTA,
}: {
  logo: ReactNode;
  description: ReactNode;
  title: ReactNode;
  CTA: ReactNode;
}) => {
  return (
    <div className="t-border t-border-solid t-border-neutral-0 t-rounded t-p-3 t-flex t-justify-between t-items-center t-w-full t-min-h-20 sm:t-shrink-0 sm:t-flex-col sm:t-items-start sm:t-gap-3">
      <div className="t-flex t-gap-3 t-items-center sm:t-min-w-0 sm:t-w-full">
        <div className="t-self-start sm:t-shrink-0">{logo}</div>
        <div className="sm:t-min-w-0">
          <p className="t-mb-1 t-text-body-lg sm:t-text-subtext">{title}</p>
          <p className="t-m-0 t-text-body-sm t-text-text-30">{description}</p>
        </div>
      </div>

      <div className="sm:t-shrink-0 sm:t-pl-11">{CTA}</div>
    </div>
  );
};

export const getConnectionInfoItem = ({
  connection,
}: {
  connection: BankConnection;
}) => {
  const isReconciliationRequired = connection.bank_accounts?.some(
    (acc) =>
      !acc.balance_matches &&
      acc.last_reconcilation_status !== RECONCILLATION_STATUS.COMPLETED
  );

  const reconcilliationRequiredStatus = isReconciliationRequired
    ? "Reconciliation required"
    : "";

  const disconnectedStatus = !connection.is_connection_available
    ? "Accounts disconnected"
    : "";

  const isAllAccountsClosed = connection.bank_accounts.every(
    (account) => account.status === BANK_ACCOUNT_STATUS.CLOSED
  );

  const canReconcile =
    isReconciliationRequired && connection.is_connection_available;

  const hasLongerName = connection.name.length > 12;

  const updatedAt = connection.last_successful_transaction_update ? (
    connection.is_connection_available && (
      <span className="t-text-body-sm t-text-dark_green-50">
        Updated {dayjs(connection.last_successful_transaction_update).fromNow()}
      </span>
    )
  ) : (
    <></>
  );

  return {
    isReconciliationRequired,
    reconcilliationRequiredStatus,
    disconnectedStatus,
    canReconcile,
    hasLongerName,
    updatedAt,
    isAllAccountsClosed,
  };
};

export const ConnectionLogo = ({
  logoUrl,
  disconnected,
  updatedAt,
  status,
  logo,
  isPlaidCard,
  isAddCard,
}: {
  disconnected?: boolean;
  logo?: ReactNode;
  logoUrl?: string;
  updatedAt?: ReactNode;
  status?: string;
  isPlaidCard?: boolean;
  isAddCard?: boolean;
}) => {
  return (
    <div
      className={classNames("t-top-0", {
        "t-shadow-light-100": isPlaidCard,
      })}
    >
      {isPlaidCard || isAddCard ? (
        logo
      ) : (
        <div className=" t-p-4 t-shadow-light-100 t-bg-white t-rounded-md t-border t-border-solid t-border-neutral-10 ">
          <div>
            {logoUrl ? (
              <img
                src={logoUrl}
                className="t-rounded-full  t-w-9 t-h-9"
                alt=""
              />
            ) : (
              logo || (
                <img
                  src={BankCircular}
                  className="t-rounded-full t-w-9 t-h-9"
                  alt=""
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const ConnectionCard = ({
  logoUrl,
  disconnected,
  tag,
  name,
  description,
  updatedAt,
  total,
  CTA,
  status,
  logo,
  isPlaidCard = false,
  isAddCard = false,
  banner,
}: {
  disconnected?: boolean;
  logo?: ReactNode;
  logoUrl?: string;
  tag?: ReactNode;
  name: ReactNode;
  description: ReactNode;
  updatedAt?: ReactNode;
  total?: number;
  CTA: ReactNode;
  status?: string;
  isPlaidCard?: boolean;
  isAddCard?: boolean;
  banner?: ReactNode;
}) => {
  return (
    <motion.div
      layout
      className={classNames(
        "t-flex t-flex-col t-flex-1 t-justify-center t-items-center t-p-9 t-rounded-lg t-relative t-overflow-visible t-min-h-[310px] t-h-full t-border t-border-solid",
        {
          "t-border-dashed t-border-neutral-20 t-bg-surface-lighter-grey":
            isAddCard,
          "t-border-neutral-10 t-bg-surface": !isAddCard,
        }
      )}
    >
      {banner && banner}
      <ConnectionLogo
        disconnected={disconnected}
        logo={logo}
        logoUrl={logoUrl}
        updatedAt={updatedAt}
        status={status}
        isPlaidCard={isPlaidCard}
        isAddCard={isAddCard}
      />
      <div className="t-mt-4 t-flex t-flex-col t-gap-2 t-justify-between t-items-center">
        <div
          className={classNames("", {
            "t-visible": tag,
            "t-invisible": !tag,
          })}
        >
          {tag}
        </div>
        <span className="t-text-subtitle-sm t-text-text-60">{name}</span>
        {description && (
          <span className="t-text-body-sm t-text-text-30 t-text-center">
            {description}
          </span>
        )}
      </div>
      {(total || total === 0) && (
        <span className="t-text-subtext t-pt-1">
          <AmountSuperScript amount={total} />
        </span>
      )}

      {updatedAt && <span className="t-pt-2">{updatedAt}</span>}

      <div className="t-mt-auto t-pt-4 t-min-w-24">{CTA}</div>
    </motion.div>
  );
};

export const TabEmptyState = ({
  title,
  description,
  onOpenConnectionModal,
  onOpenSubscriptionModal,
}: {
  title: string;
  description: string;
  onOpenConnectionModal: () => void;
  onOpenSubscriptionModal: () => void;
}) => {
  return (
    <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-2 t-py-10">
      <img src={EmptyDataSource} alt="No data sources found" />
      <div className="t-text-subtex">{title}</div>
      <p className="t-text-body-sm t-text-text-30">{description}</p>
      <LockedUi feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}>
        <PermissionBasedUI
          feature={CONNECT_BANK_ACCOUNT}
          blockedUI={
            <Button
              onClick={onOpenSubscriptionModal}
              customType="primary"
              size="small"
            >
              <div className="t-flex t-items-center t-gap-2">
                <Diamond />
                Connect
              </div>
            </Button>
          }
        >
          <Button
            onClick={onOpenConnectionModal}
            customType="primary"
            size="small"
          >
            Connect
          </Button>
        </PermissionBasedUI>
      </LockedUi>
    </div>
  );
};

export const AccountBanner = ({
  totalBalance,
  totalAccounts,
}: {
  totalBalance: number;
  totalAccounts: number;
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-5 t-mb-6">
      <div className="t-flex t-items-center t-justify-center t-py-5 t-text-center t-flex-col t-gap-1 t-bg-[url('/src/static/images/TotalbalanceUI.svg')] t-bg-no-repeat t-bg-cover t-rounded-lg t-w-full t-overflow-hidden t-bg-center t-border t-border-solid t-border-blue-10">
        <div className="t-text-subtext t-text-text-black">
          {<AmountSuperScript amount={totalBalance} />}
        </div>
        <div className="t-text-body-sm t-text-text-60 t-flex t-gap-1">
          <span>
            Total balance of {pluralize(totalAccounts, "account", "accounts")}
          </span>
          <ToolTip text="This balance does not include credit card balances.">
            <span className="t-text-text-30">
              <Info />
            </span>
          </ToolTip>
        </div>
      </div>
    </div>
  );
};

const FilterByDropdown = ({
  options,
  value,
  onChange,
}: {
  options: OptionData[];
  value: string;
  onChange: (value: string) => void;
}) => {
  const selected =
    options.find((option) => option.value === value) ?? options[0];

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button
          type="button"
          className="all:unset t-flex t-cursor-pointer t-items-center t-justify-center t-gap-1 t-rounded-md t-border t-border-solid t-border-neutral-10 t-bg-surface t-px-2 t-py-1.5 t-shadow-xs"
        >
          <span className="t-whitespace-nowrap t-text-body-sm t-font-medium t-leading-4 t-text-text-60">
            {selected?.label}
          </span>
          <span className="t-flex t-p-0.5 t-text-text-30">
            <ArrowDown size="12" color="currentColor" strokeWidth="1.5" />
          </span>
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content align="start" sideOffset={4}>
          {options.map((option) => (
            <Dropdown.Item
              key={String(option.value)}
              onSelect={() => onChange(String(option.value))}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

export const DataSourcesList = () => {
  usePageTitle("Data Sources");
  const { update } = useUpdateQuery();
  const query = useQuery();
  const bankConnectModal = query.get("modal");
  const { url, path } = useRouteMatch();
  const history = useHistory();
  const { search } = useLocation();
  const connectionModal = useModal();
  const subscriptionModal = useModal();
  const { isBankReconnectRequired } = useActionItems();

  const closeConnectionModal = () => {
    connectionModal.close();
    update({ query: "modal", value: null });
  };

  const isOpen =
    Boolean(bankConnectModal === BANK_CONNECTION_MODAL) ||
    connectionModal.isOpen;
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { postMessage } = useBroadcastChannel(
    BANK_CONNECTION_SUCCESS_BROADCAST_CHANNEL
  );

  const {
    data: bookkeepingSettings,
    isLoading: isLoadingBookkeepingSettings,
    isSuccess: isBookkeepingSettingsLoaded,
  } = useGetBookkeepingSettingsQuery(
    {
      entityId: entityId!,
    },
    { skip: !groupId || !entityId }
  );

  const {
    data: plaidConnections,
    isLoading: bankAccountsLoading,
    isSuccess: banksAccountsLoaded,
  } = useGetEntityBanksQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
    },
    { skip: !groupId || !entityId }
  );

  const { bankConnections, stripeConnectedBank, allBankConnections } =
    useDataSourceSegregate(plaidConnections!);

  const { data: apArConnections } = useGetEntityBanksQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
      type: "AP_AR",
    },
    { skip: !groupId || !entityId }
  );

  const { bankConnections: apArBankConnections } = useDataSourceSegregate(
    apArConnections!
  );

  const onConnectSuccess = () => {
    closeConnectionModal();
    postMessage(BANK_CONNECTED_BROADCAST_MESSAGE);
  };

  const onMigrationSuccess = () => {
    closeConnectionModal();
    postMessage(BANK_CONNECTED_BROADCAST_MESSAGE);
  };

  const isEmpty =
    !stripeConnectedBank &&
    bankConnections.length === 0 &&
    !bookkeepingSettings?.gusto_access;

  const onTabChange = (value: string) => {
    update({
      query: "type",
      value: value,
    });
  };

  const dataSourceFilters: OptionData[] = [
    { label: "All sources", value: ACCOUNT_CONNECTION_TYPE.ALL },
    {
      label: "Bank & Credit Cards",
      value: ACCOUNT_CONNECTION_TYPE.BANK_AND_CREDIT_CARD,
    },
    { label: "Revenue", value: ACCOUNT_CONNECTION_TYPE.REVENUE },
    { label: "Payroll", value: ACCOUNT_CONNECTION_TYPE.PAYROLL },
    { label: "AP & AR", value: ACCOUNT_CONNECTION_TYPE.AP_AR },
  ];

  const activeFilter = query.get("type") || ACCOUNT_CONNECTION_TYPE.ALL;

  return (
    <DashboardLayout
      contentProps={{ className: "t-mb-6" }}
      header={
        <Header
          v2
          title={
            <div className="t-flex t-gap-2 t-items-center">
              Data Sources{" "}
              {isBankReconnectRequired && <ActionRequiredPopOver />}
            </div>
          }
          right={
            !isEmpty && (
              <LockedUi feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}>
                <PermissionBasedUI
                  feature={CONNECT_BANK_ACCOUNT}
                  blockedUI={
                    <Button
                      onClick={subscriptionModal.open}
                      customType="primary"
                      size="small"
                    >
                      <div className="t-flex t-items-center t-gap-2">
                        <Diamond />
                        Connect
                      </div>
                    </Button>
                  }
                >
                  <Button
                    onClick={connectionModal.open}
                    customType="primary"
                    size="small"
                  >
                    Connect
                  </Button>
                </PermissionBasedUI>
              </LockedUi>
            )
          }
        />
      }
    >
      <Async.Root
        isSuccess={banksAccountsLoaded && isBookkeepingSettingsLoaded}
        isLoading={bankAccountsLoading || isLoadingBookkeepingSettings}
        isEmpty={isEmpty}
      >
        <Async.Empty>
          <EmptyScreen
            text={
              <div className="t-text-text-60 t-flex t-flex-col">
                <span className="t-text-subtext">No data sources found</span>
                <span className="t-text-body-sm">
                  Set up banks,cards and revenue integrations to seamlessly
                  bring in your data
                </span>
              </div>
            }
            cta={
              <LockedUi feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}>
                <PermissionBasedUI
                  align="end"
                  feature={CONNECT_BANK_ACCOUNT}
                  blockedUI={
                    <BlockedButton size="small">Connect</BlockedButton>
                  }
                >
                  <div className="t-pt-4">
                    <Button
                      onClick={connectionModal.open}
                      customType="primary"
                      size="small"
                    >
                      Connect
                    </Button>
                  </div>
                </PermissionBasedUI>
              </LockedUi>
            }
          >
            <img src={EmptyDataSource} alt="No data sources found" />
          </EmptyScreen>
        </Async.Empty>
        <Async.Success>
          <Tab.Root
            value={query.get("type") || ACCOUNT_CONNECTION_TYPE.ALL}
            className="t-h-full t-flex t-flex-col t-gap-3"
            onValueChange={onTabChange}
          >
            <div className="t-flex t-flex-shrink-0 t-items-center t-gap-2">
              <span className="t-text-body-sm t-text-text-30">Filter by:</span>
              <FilterByDropdown
                options={dataSourceFilters}
                value={activeFilter}
                onChange={onTabChange}
              />
            </div>
            <Tab.Content
              value={ACCOUNT_CONNECTION_TYPE.ALL}
              className="t-overflow-y-auto"
            >
              <AllAccount
                groupId={groupId}
                onMigrationSuccess={onMigrationSuccess}
              />
            </Tab.Content>
            <Tab.Content
              value={ACCOUNT_CONNECTION_TYPE.BANK_AND_CREDIT_CARD}
              className="t-overflow-y-auto"
            >
              <BankAndCreditCard
                groupId={groupId}
                onMigrationSuccess={onMigrationSuccess}
                onOpenConnectionModal={connectionModal.open}
                onOpenSubscriptionModal={subscriptionModal.open}
              />
            </Tab.Content>
            <Tab.Content
              value={ACCOUNT_CONNECTION_TYPE.REVENUE}
              className="t-overflow-y-auto"
            >
              <Revenue
                groupId={groupId}
                onOpenConnectionModal={connectionModal.open}
                onOpenSubscriptionModal={subscriptionModal.open}
              />
            </Tab.Content>
            <Tab.Content
              value={ACCOUNT_CONNECTION_TYPE.PAYROLL}
              className="t-overflow-y-auto"
            >
              <Payroll
                groupId={groupId}
                onOpenConnectionModal={connectionModal.open}
                onOpenSubscriptionModal={subscriptionModal.open}
              />
            </Tab.Content>
            <Tab.Content
              value={ACCOUNT_CONNECTION_TYPE.AP_AR}
              className="t-overflow-y-auto"
            >
              <APandAR
                groupId={groupId}
                onOpenConnectionModal={connectionModal.open}
                onOpenSubscriptionModal={subscriptionModal.open}
              />
            </Tab.Content>
          </Tab.Root>
          <EditBankAccount />
          <Switch>
            <Route path={`${path}/:dataSourceId`}>
              {({ match }) => {
                const dataSourceId = match?.params.dataSourceId;

                const isGusto = dataSourceId === GUSTO;

                if (isGusto) {
                  return (
                    <GustoSlider
                      open={true}
                      close={() => history.push(`${url}${search}`)}
                      groupId={groupId}
                      entityId={entityId}
                    />
                  );
                }

                const apArConnection = apArBankConnections.find(
                  (bank) => bank.uuid === dataSourceId
                );

                if (apArConnection) {
                  return (
                    <BillComSlider
                      connection={apArConnection}
                      onClose={() => history.push(`${url}${search}`)}
                    />
                  );
                }

                const connection = allBankConnections.find(
                  (bank) => bank.uuid === dataSourceId
                );

                if (connection) {
                  return (
                    <DataSourceView
                      connection={connection}
                      onClose={() => history.push(`${url}${search}`)}
                    />
                  );
                }

                if (!connection) {
                  return null;
                }
              }}
            </Route>
          </Switch>
        </Async.Success>
        <section>
          <ConnectDataSourcesRoot
            isOpen={isOpen}
            closeConnectionModal={closeConnectionModal}
            onConnectSuccess={onConnectSuccess}
            currentTabType={
              (query.get("type") as AccountConnectionType) || "ALL"
            }
          />
          <BooksSoftwareSubscriptionsModal
            open={subscriptionModal.isOpen}
            onClose={() => subscriptionModal.close()}
            entityId={entityId}
            onProductSubscribe={() => subscriptionModal.close()}
            modalDescription={
              <ModalDescriptionWrapper value="Unlock the ability to connect more than 2 bank accounts and access Stripe integration with the Books Standard Plan" />
            }
          />
        </section>
      </Async.Root>
    </DashboardLayout>
  );
};
